import { useEffect, useRef, useState } from 'react';
import { FileText, Receipt, Save, Plus, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMsg } from '@/i18n/msg';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FieldLabel, MonoLabel, numericInputProps, StatusChip, useNumericDraft } from '@/components/editorial';
import { MODAL_INPUT_CLASS, ModalFooter, ModalHeader } from '@/components/admin/editorial-modal';
import { PendingDecision } from '@/components/editorial/PendingDecision';
import {
  EXPENSE_CATEGORIES,
  EXPENSE_CATEGORY_TOKENS,
  formatUsd,
  ledgerStatus,
  type ExpenseCategory,
} from '@/lib/bills-display';
import type { ClaimFamilyEdits, ReimbursementClaim } from '@/types/api';
import { formatNumber } from '@/i18n/intl';
import { useReceiptThumbnail } from './useReceiptThumbnail';
import { MAX_UPLOAD_MB, UPLOAD_ACCEPT, uploadRefusal } from '@/lib/uploadTypes';

/**
 * The details behind a ledger row (Finances collector spec 2026-09-10 §4.4):
 * merchant · amount · date · category · what it was for · the receipt.
 *
 * Three modes from one component:
 *  - new (no `claim`): "Add expense" — a hand-entered record; a receipt may be
 *    picked here and travels with the save.
 *  - edit (`claim` in draft / needs_info): every field editable, receipt
 *    replaceable. Saving a complete record submits it — the caller does not
 *    need a second button.
 *  - read-only (`claim` past needs_info): the record is with TRF; the facts
 *    are shown, nothing is editable. A NOT-APPROVED receipt is read-only too,
 *    but it is not "with TRF": its footer says so and offers "Add a new
 *    receipt" (`onAddNew`) — `(?)` receipt-not-approved-next-step.
 */
export interface ExpenseFormValues extends Required<Pick<ClaimFamilyEdits, 'vendor' | 'amount' | 'expense_date' | 'category' | 'note'>> {}

export interface ExpenseDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claim?: ReimbursementClaim | null;
  /**
   * Save. `file` is the receipt picked in the dialog, if any. `reply` is the
   * family's answer to TRF's needs_info message ("Reply and send back"); it
   * sends the claim back to TRF even when nothing else changed.
   */
  onSubmit: (values: ExpenseFormValues, file: File | null, reply?: string) => void;
  isSubmitting?: boolean;
  /** A not-approved receipt's next step: open the add-expense flow. */
  onAddNew?: () => void;
}

/** The server caps a reply like TRF's message (PATCH /reimbursements/:id, 500). */
const REPLY_MAX = 500;

const EMPTY: ExpenseFormValues = { vendor: '', amount: null, expense_date: '', category: null, note: '' };
const FAMILY_EDITABLE = new Set(['draft', 'needs_info']);

function toForm(claim: ReimbursementClaim): ExpenseFormValues {
  return {
    vendor: claim.vendor ?? '',
    amount: claim.amount ?? null,
    expense_date: claim.expense_date ? claim.expense_date.slice(0, 10) : '',
    category: (claim.category as ExpenseCategory | null) ?? null,
    note: claim.note ?? '',
  };
}

export function ExpenseDetailsDialog({ open, onOpenChange, claim, onSubmit, isSubmitting, onAddNew }: ExpenseDetailsDialogProps) {
  const { t, i18n } = useTranslation(['money', 'common']);
  const resolve = useMsg();
  const [form, setForm] = useState<ExpenseFormValues>(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  // #2380: a picked file the server would refuse, with its reason; never sent.
  const [refused, setRefused] = useState<{ name: string; reason: string } | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const isNew = !claim;
  const readOnly = Boolean(claim && !FAMILY_EDITABLE.has(claim.status));
  // #2632: decided against — not "with TRF". The footer names the next step.
  const notApproved = claim?.status === 'rejected';
  // A claim TRF sent back with a message can go back with only a reply.
  const canReply = claim?.status === 'needs_info';
  // The draft keeps a typed "23." alive between keystrokes (DECISIONS 2026-08-27:
  // an integer-mode sanitiser turned 3.5 into 35 — a silent 10× on a money field).
  const amountDraft = useNumericDraft(form.amount ?? null, (n) => setForm((f) => ({ ...f, amount: n })), { decimal: true });
  const thumb = useReceiptThumbnail(claim?.source_document_id ?? null, claim?.document?.mime_type ?? null);

  useEffect(() => {
    if (!open) return;
    setForm(claim ? toForm(claim) : EMPTY);
    setFile(null);
    setRefused(null);
    setReply('');
  }, [open, claim]);

  // #2380: the same check and the same per-file sentence as ReceiptCapture.
  // A refused file is named and dropped, so the save never carries it.
  const pickReceipt = (picked: File | null) => {
    if (!picked) { setFile(null); setRefused(null); return; }
    const refusal = uploadRefusal(picked);
    if (refusal) {
      setRefused({
        name: picked.name,
        reason: refusal === 'type' ? t('capture.wrongType') : t('capture.tooLarge', { size: MAX_UPLOAD_MB }),
      });
      return;
    }
    setRefused(null);
    setFile(picked);
  };

  useEffect(() => {
    // jsdom has no object URLs; a missing preview is fine, a crash is not.
    if (!file || !file.type.startsWith('image/') || typeof URL.createObjectURL !== 'function') { setLocalPreview(null); return; }
    const url = URL.createObjectURL(file);
    setLocalPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const hasReceipt = Boolean(claim?.source_document_id) || Boolean(file);
  const complete = Boolean(form.vendor?.trim()) && Number(form.amount) > 0 && Boolean(form.expense_date) && Boolean(form.category) && hasReceipt;
  const canSave = !readOnly && (
    Boolean(form.vendor?.trim()) || Number(form.amount) > 0 || Boolean(form.expense_date) || Boolean(form.category) || Boolean(form.note?.trim()) || Boolean(file)
  );
  const status = claim ? ledgerStatus(claim) : null;
  const preview = localPreview ?? thumb;
  const isPdf = (file?.type ?? claim?.document?.mime_type) === 'application/pdf';

  const footerNote = notApproved
    ? t('details.footerNotApproved')
    : readOnly
    ? t('details.footerReadOnly')
    : complete
      ? t('details.footerComplete')
      : hasReceipt
        ? t('details.footerMissingDetails')
        : t('details.footerMissingReceipt');
  // The OCR summary is the model's English reading of the receipt (data, not
  // UI). A non-English reader gets no summary rather than an English line
  // inside a translated dialog (#2109 L4); the fields it filled still show.
  const isEnglish = i18n.language === 'en' || i18n.language.startsWith('en-');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl flex flex-col p-0 gap-0 max-h-[90vh]" data-testid="expense-details-dialog">
        <ModalHeader
          icon={Receipt}
          eyebrow={isNew ? t('details.eyebrowNew') : readOnly ? t('details.eyebrowReadOnly') : t('details.eyebrowEdit')}
          title={isNew ? t('details.titleNew') : (claim?.vendor || t('details.titleFallback'))}
          description={status ? <StatusChip tone={status.tone}>{resolve({ key: status.labelKey })}</StatusChip> : undefined}
        />

        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-5 min-h-0">
          {/* #2403 (D-2403 option A): a rejected receipt says why, in the
              words TRF typed for the family. Staff-typed, so it shows as
              written in every language; the staff-only review notes are
              never on this payload. */}
          {/* A claim sent back as needs_info carries TRF's message in the
              same field, saying what to add or fix. */}
          {(claim?.status === 'rejected' || claim?.status === 'needs_info') && claim.decision_reason && (
            <div
              className={claim.status === 'rejected'
                ? 'rounded-lg border border-status-danger-border bg-status-danger px-4 py-3'
                : 'rounded-lg border border-status-warn-border bg-status-warn px-4 py-3'}
              data-testid="expense-decision-reason"
            >
              <FieldLabel as="span" className="mb-1">
                {claim.status === 'rejected' ? t('details.decisionReasonLabel') : t('details.needsInfoReasonLabel')}
              </FieldLabel>
              <p className="text-body text-foreground whitespace-pre-line break-words">{claim.decision_reason}</p>
            </div>
          )}

          {/* The family's answer to TRF's message. A receipt that was fine
              but needed an explanation goes back with the reply alone. */}
          {canReply && (
            <div className="space-y-2" data-testid="expense-reply">
              <Field label={t('details.replyLabel')}>
                <textarea
                  value={reply}
                  maxLength={REPLY_MAX}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={t('details.replyPlaceholder')}
                  rows={3}
                  className={cn(inputClass(false), 'resize-none')}
                />
              </Field>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-caption text-stevens-ink-3">{t('details.replyHint')}</p>
                <Button
                  size="sm"
                  variant="brand"
                  disabled={!reply.trim() || isSubmitting}
                  onClick={() => onSubmit(form, file, reply.trim())}
                  data-testid="expense-reply-send"
                >
                  <Send className="h-3.5 w-3.5 me-1.5" />
                  {t('details.replySend')}
                </Button>
              </div>
            </div>
          )}

          {/* The receipt slot — the one fact TRF cannot do without. */}
          <Field label={t('details.receiptLabel')} group>
            <div className="flex items-center gap-3">
              <div className={cn(
                'h-16 w-16 shrink-0 overflow-hidden rounded-md border flex items-center justify-center',
                hasReceipt ? 'border-border/60 bg-muted/30' : 'border-dashed border-status-warn-border',
              )}>
                {preview ? (
                  <img src={preview} alt={t('details.receiptAlt')} className="h-full w-full object-cover" />
                ) : hasReceipt ? (
                  <FileText className="h-5 w-5 text-stevens-ink-3" aria-label={isPdf ? t('details.receiptPdf') : t('details.receiptAlt')} />
                ) : (
                  <Receipt className="h-5 w-5 text-status-warn-fg" aria-label={t('details.receiptMissing')} />
                )}
              </div>
              <div className="min-w-0 flex-1 text-caption text-stevens-ink-3">
                {file ? (
                  <span className="truncate block">{file.name}</span>
                ) : claim?.document?.filename ? (
                  <span className="truncate block">{claim.document.filename}</span>
                ) : (
                  <span>{t('details.receiptNone')}</span>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    className="mt-1 text-stevens-indigo hover:underline underline-offset-2"
                    onClick={() => fileRef.current?.click()}
                  >
                    {hasReceipt ? t('details.replaceReceipt') : t('details.addReceipt')}
                  </button>
                )}
                {/* #2618: read-only, nothing can save a picked file — so there
                    is no input for Tab or a screen reader to land on. */}
                {!readOnly && (
                  <input
                    ref={fileRef}
                    data-testid="details-receipt-input"
                    type="file"
                    accept={UPLOAD_ACCEPT}
                    className="sr-only"
                    aria-label={t('details.receiptFile')}
                    onChange={(e) => { pickReceipt(e.target.files?.[0] ?? null); e.target.value = ''; }}
                  />
                )}
                {refused && (
                  <p role="alert" data-testid="details-receipt-refusal" className="mt-1 text-status-danger-fg">
                    <span className="block truncate">{refused.name}</span>
                    <span className="block break-words">{refused.reason}</span>
                  </p>
                )}
              </div>
            </div>
          </Field>

          <Field label={t('details.vendorLabel')}>
            <input
              type="text"
              value={form.vendor ?? ''}
              readOnly={readOnly}
              onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
              placeholder={t('details.vendorPlaceholder')}
              className={inputClass(readOnly)}
              autoFocus={!readOnly}
            />
          </Field>

          <div className="grid grid-cols-[1fr_160px] gap-3">
            <Field label={t('details.amountLabel')}>
              <input
                {...numericInputProps({ decimal: true, max: 999999 })}
                {...amountDraft}
                readOnly={readOnly}
                placeholder={amountPlaceholder()}
                className={cn(inputClass(readOnly), 'font-mono')}
              />
            </Field>
            <Field label={t('details.whenLabel')}>
              <input
                type="date"
                value={form.expense_date ?? ''}
                readOnly={readOnly}
                onChange={(e) => setForm((f) => ({ ...f, expense_date: e.target.value }))}
                className={inputClass(readOnly)}
              />
            </Field>
          </div>

          <Field label={t('details.categoryLabel')} group>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {EXPENSE_CATEGORIES.map((c) => {
                const token = EXPENSE_CATEGORY_TOKENS[c];
                const isActive = form.category === c;
                return (
                  <button
                    key={c}
                    type="button"
                    disabled={readOnly}
                    aria-pressed={isActive}
                    onClick={() => setForm((f) => ({ ...f, category: c }))}
                    className={cn(
                      'flex items-center gap-1.5 rounded-lg border px-3 py-2 text-caption text-left transition-colors',
                      isActive ? 'border-stevens-indigo bg-stevens-indigo/5 text-foreground' : 'border-border hover:bg-muted',
                      readOnly && 'cursor-default hover:bg-transparent',
                    )}
                  >
                    <span className={cn('w-[7px] h-[7px] shrink-0 rounded-full', token.rail)} />
                    {resolve({ key: token.labelKey })}
                  </button>
                );
              })}
            </div>
          </Field>

          <div>
            <Field label={t('details.noteLabel')}>
              <textarea
                value={form.note ?? ''}
                readOnly={readOnly}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder={t('details.notePlaceholder')}
                rows={2}
                className={cn(inputClass(readOnly), 'resize-none')}
              />
            </Field>
            {/* #2409: a scanned receipt fills this note only for an English
                reader. Outside the <label>, so the note's name stays its label. */}
            {claim?.ocr_extracted && (
              <div className="mt-1 flex justify-end">
                <PendingDecision id="receipt-note-english-only" side="top" />
              </div>
            )}
          </div>

          {claim?.ocr_extracted?.summary && !readOnly && isEnglish && (
            <p className="text-caption text-stevens-ink-3">
              {claim.ocr_extracted.amount != null
                ? t('details.ocrWithAmount', { summary: claim.ocr_extracted.summary, amount: formatUsd(claim.ocr_extracted.amount) })
                : t('details.ocr', { summary: claim.ocr_extracted.summary })}
            </p>
          )}
        </div>

        <ModalFooter
          lead={
            <div className="flex min-w-0 items-center gap-1.5">
              <MonoLabel>{footerNote}</MonoLabel>
              {notApproved && <PendingDecision id="receipt-not-approved-next-step" side="top" />}
            </div>
          }
        >
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {readOnly ? t('common:actions.close') : t('common:actions.cancel')}
          </Button>
          {notApproved && onAddNew && (
            <Button size="sm" variant="brand" onClick={onAddNew} data-testid="expense-add-new-receipt">
              <Plus className="h-3.5 w-3.5 me-1.5" />
              {t('details.addNewReceipt')}
            </Button>
          )}
          {!readOnly && (
            <Button
              size="sm"
              variant="brand"
              disabled={!canSave || isSubmitting}
              onClick={() => onSubmit(form, file)}
            >
              {isNew ? <Plus className="h-3.5 w-3.5 mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              {isSubmitting ? t('common:states.saving') : isNew ? t('details.add') : t('common:actions.saveChanges')}
            </Button>
          )}
        </ModalFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "0.00" in en, "0,00" where the locale writes a decimal comma (#2109 R3-B6). */
function amountPlaceholder(): string {
  return formatNumber(0, { minimumFractionDigits: 2 });
}

function inputClass(readOnly: boolean) {
  return cn(
    MODAL_INPUT_CLASS,
    readOnly && 'bg-muted/30 text-stevens-ink-2 focus:border-input',
  );
}

/**
 * `<label>` for a single input; `group` for a field made of buttons — a
 * `<button>` is a labelable element, so a `<label>` wrapping several would
 * hand its whole text to the first one as its accessible name.
 */
function Field({ label, children, group = false }: { label: string; children: React.ReactNode; group?: boolean }) {
  if (group) {
    return (
      <div className="block" role="group" aria-label={label}>
        <FieldLabel as="span" className="mb-1.5">{label}</FieldLabel>
        {children}
      </div>
    );
  }
  return (
    <label className="block">
      <FieldLabel as="span" className="mb-1.5">{label}</FieldLabel>
      {children}
    </label>
  );
}
